import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { vendorAccessApi, type VendorAccess } from '@/api/vendorAccess';
import { api } from '@/api/client';
import { currentAuthOwner } from '@/utils/authOwner';
import { useAuthStore } from './useStore';

const DENIED: VendorAccess = { can_access: false, can_manage_access: false };

/** The server is authoritative; a role or a saved browser preference never grants access. */
export function useVendorAccess() {
  const { user, token, isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();
  const userId = user?.id;
  const organizationId = user?.organization_id;
  const enabled = !!(isAuthenticated && token && userId && user?.role !== 'guest');
  const queryKey = ['vendor-access', userId, organizationId] as const;
  const query = useQuery({
    queryKey,
    enabled,
    queryFn: async ({ signal }) => {
      const owner = currentAuthOwner();
      const access = await vendorAccessApi.getAccess(signal);
      if (
        !api.ownsCurrentSession(owner) ||
        useAuthStore.getState().user?.organization_id !== organizationId
      ) {
        throw new Error('The signed-in account changed.');
      }
      return access;
    },
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 30_000,
  });

  // Cancel before eviction so a request started before revocation cannot refill the cache.
  const clearVendorRecords = useCallback(() => {
    for (const prefix of ['vendor-management', 'vendor-workspaces', 'vendor-access-users']) {
      const filter = { queryKey: [prefix, userId, organizationId] };
      void queryClient.cancelQueries(filter);
      queryClient.removeQueries(filter);
    }
  }, [organizationId, queryClient, userId]);

  const denyAccess = useCallback(() => {
    const current = useAuthStore.getState();
    const owner = { userId: userId ?? null, token, isAuthenticated };
    if (!api.ownsCurrentSession(owner) || current.user?.organization_id !== organizationId) return;
    void queryClient.cancelQueries({ queryKey: ['vendor-access', userId, organizationId] });
    queryClient.setQueryData<VendorAccess>(['vendor-access', userId, organizationId], DENIED);
    clearVendorRecords();
  }, [clearVendorRecords, isAuthenticated, organizationId, queryClient, token, userId]);

  const canAccess = enabled && query.isSuccess && query.data.can_access === true;
  const canManageAccess = canAccess && query.data?.can_manage_access === true;
  useEffect(() => {
    if (!enabled || query.isError || (query.isSuccess && !query.data.can_access)) {
      clearVendorRecords();
    }
  }, [clearVendorRecords, enabled, query.isError, query.isSuccess, query.data?.can_access]);

  return {
    canAccess,
    canManageAccess,
    isPending: enabled && query.isPending,
    isError: query.isError,
    refetch: query.refetch,
    denyAccess,
  };
}
